import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, Plus, Trash2, GripVertical, Info } from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Input, Select, ErrorNotice, Loading,
} from '../../components/ui/index.js';
import { PageHero } from '../../components/PageHero.jsx';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';

const EASE = [0.2, 0.7, 0.2, 1];
const MAX_FIELDS = 8;

const blank = () => ({ label: '', type: 'text', options: [], required: false, enabled: true });

/**
 * Fields the merchant captures on every sale.
 *
 * Deliberately capped at eight. A counter form longer than that is a queue, and
 * the fastest way to make staff stop filling any of them in is to ask for too
 * many.
 */
export function CustomFieldsPage() {
  const toast = useToast();
  const { data, error, loading, refetch } = useResource('/dashboard/custom-fields');
  const [fields, setFields] = useState([]);
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty);

  useEffect(() => { if (data) { setFields(data); setDirty(false); } }, [data]);

  const save = useMutation(async () => {
    const payload = fields
      .filter((f) => f.label.trim())
      .map((f) => ({
        // Sending the key back is what preserves history: the server honours it
        // only for keys it issued, so a renamed label keeps its payments.
        ...(f.key ? { key: f.key } : {}),
        label: f.label.trim(),
        type: f.type,
        options: f.type === 'select' ? f.options.filter(Boolean) : [],
        required: f.required,
        enabled: f.enabled,
      }));

    await http.put('/dashboard/custom-fields', { fields: payload });
    toast.success('Fields saved', { description: 'They appear on the till straight away.' });
    setDirty(false);
    refetch();
  });

  const update = (index, patch) => {
    setFields((f) => f.map((field, i) => (i === index ? { ...field, ...patch } : field)));
    setDirty(true);
  };

  const remove = (index) => {
    setFields((f) => f.filter((_, i) => i !== index));
    setDirty(true);
  };

  const move = (index, by) => {
    const target = index + by;
    if (target < 0 || target >= fields.length) return;
    setFields((f) => {
      const next = [...f];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  return (
    <>
      <PageHero
        icon={SlidersHorizontal}
        chip="Get paid"
        title="Sale details"
        blurb="Extra things to record on every sale — a table number, a salesperson, a delivery route. They appear on the till and on the payment, and unlike a free note you can filter and total by them."
      />

      {loading && <Loading label="Loading fields…" />}
      {error && <ErrorNotice error={error} onRetry={refetch} title="Could not load your fields" />}

      {data && (
        <Card>
          <CardHeader
            title="Your fields"
            blurb={`${fields.length} of ${MAX_FIELDS} used`}
            actions={
              <Button
                variant="secondary"
                icon={Plus}
                disabled={fields.length >= MAX_FIELDS}
                onClick={() => { setFields((f) => [...f, blank()]); setDirty(true); }}
              >
                Add field
              </Button>
            }
          />

          <CardBody className="space-y-4">
            {save.error && <ErrorNotice error={save.error} title="Could not save" />}

            {fields.length === 0 && (
              <div className="flex items-start gap-2.5 rounded-lg border border-line bg-surface-2 px-3.5 py-3">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-ink-soft">
                  No fields yet. Staff can still leave a free-text note on any sale — these are for the
                  things you want to report on afterwards.
                </p>
              </div>
            )}

            <AnimatePresence initial={false}>
              {fields.map((field, index) => (
                <motion.div
                  key={field.key ?? `new-${index}`}
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.22, ease: EASE }}
                  className="overflow-hidden"
                >
                  <div className="rounded-xl border border-line bg-surface-2 p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col pt-1.5">
                        <button
                          type="button"
                          onClick={() => move(index, -1)}
                          disabled={index === 0}
                          className="text-faint transition-colors hover:text-ink disabled:opacity-30"
                          aria-label="Move up"
                        >
                          <GripVertical className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid flex-1 gap-3 sm:grid-cols-[1fr_140px]">
                        <label className="block">
                          <span className="label">Label</span>
                          <Input
                            value={field.label}
                            placeholder="Table number"
                            maxLength={60}
                            onChange={(e) => update(index, { label: e.target.value })}
                          />
                        </label>

                        <label className="block">
                          <span className="label">Type</span>
                          <Select value={field.type} onChange={(e) => update(index, { type: e.target.value })}>
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="select">Dropdown</option>
                          </Select>
                        </label>

                        {field.type === 'select' && (
                          <label className="block sm:col-span-2">
                            <span className="label">Options</span>
                            <Input
                              value={field.options.join(', ')}
                              placeholder="Asha, Ravi, Priya"
                              onChange={(e) => update(index, {
                                options: e.target.value.split(',').map((o) => o.trim()),
                              })}
                            />
                            <span className="mt-1 block text-xs text-muted">Separate with commas.</span>
                          </label>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="mt-7 shrink-0 rounded-md p-1.5 text-faint transition-colors hover:bg-red-50 hover:text-red-600"
                        aria-label={`Remove ${field.label || 'field'}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-line-soft pt-3">
                      <label className="flex items-center gap-2 text-xs text-ink-soft">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => update(index, { required: e.target.checked })}
                        />
                        Required — a sale cannot be rung up without it
                      </label>
                      <label className="flex items-center gap-2 text-xs text-ink-soft">
                        <input
                          type="checkbox"
                          checked={field.enabled}
                          onChange={(e) => update(index, { enabled: e.target.checked })}
                        />
                        Show on the till
                      </label>
                      {field.key && (
                        <span className="ml-auto font-mono text-[11px] text-faint">{field.key}</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            <div className="flex items-center gap-3 border-t border-line pt-4">
              <Button loading={save.loading} disabled={!dirty} onClick={() => save.mutate().catch(() => {})}>
                Save fields
              </Button>
              {dirty && <span className="text-xs text-muted">Unsaved changes</span>}
              {/*
                * Renaming is safe and worth saying so: a merchant who thinks a
                * rename will lose their history will live with a bad label
                * forever rather than risk it.
                */}
              <span className="ml-auto hidden text-xs text-muted sm:inline">
                Renaming a label keeps every sale already filed under it.
              </span>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
}

export default CustomFieldsPage;
